import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, LayoutGrid, Layers, Image, RefreshCw, Images, SlidersHorizontal } from 'lucide-react';
import { Logo } from '../components/ui/Logo';

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    fontFamily: "'Inter', 'Roboto', sans-serif",
    color: '#1F2937',
    backgroundColor: '#FFFFFF',
  },
  // Navbar
  nav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  navLogo: {
    height: '36px',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '32px',
  },
  navLink: {
    color: '#6B7280',
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: 500,
    letterSpacing: '-0.01em',
  },
  navCta: {
    backgroundColor: '#4ADE80',
    color: '#1F2937',
    padding: '10px 20px',
    borderRadius: '8px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
  },
  // Hero
  hero: {
    textAlign: 'center' as const,
    padding: '80px 24px 60px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  heroH1: {
    fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    marginBottom: '20px',
    color: '#1F2937',
  },
  heroGreen: {
    color: '#4ADE80',
  },
  heroSubtitle: {
    fontSize: '1.2rem',
    color: '#6B7280',
    lineHeight: 1.6,
    maxWidth: '600px',
    margin: '0 auto 36px',
  },
  heroCtas: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap' as const,
  },
  primaryBtn: {
    backgroundColor: '#4ADE80',
    color: '#1F2937',
    padding: '14px 32px',
    borderRadius: '8px',
    textDecoration: 'none',
    fontSize: '16px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  secondaryBtn: {
    backgroundColor: 'transparent',
    color: '#1F2937',
    padding: '14px 32px',
    borderRadius: '8px',
    textDecoration: 'none',
    fontSize: '16px',
    fontWeight: 600,
    border: '2px solid #E5E7EB',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  // Section
  section: {
    padding: '80px 24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  sectionGray: {
    backgroundColor: '#F9FAFB',
    padding: '80px 24px',
  },
  sectionInner: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  sectionTitle: {
    fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
    fontWeight: 700,
    textAlign: 'center' as const,
    marginBottom: '12px',
    letterSpacing: '-0.02em',
  },
  sectionSubtitle: {
    fontSize: '1.1rem',
    color: '#6B7280',
    textAlign: 'center' as const,
    marginBottom: '48px',
    maxWidth: '600px',
    margin: '0 auto 48px',
  },
  // Features grid
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '24px',
  },
  featureCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    padding: '28px',
  },
  featureIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: '#ECFDF5',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  featureTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: '8px',
    color: '#1F2937',
  },
  featureDesc: {
    fontSize: '0.95rem',
    color: '#6B7280',
    lineHeight: 1.6,
  },
  // How it works
  stepsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '32px',
  },
  stepCard: {
    textAlign: 'center' as const,
    padding: '32px 24px',
  },
  stepNumber: {
    width: '56px',
    height: '56px',
    backgroundColor: '#4ADE80',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#1F2937',
  },
  stepTitle: {
    fontSize: '1.2rem',
    fontWeight: 600,
    marginBottom: '10px',
    color: '#1F2937',
  },
  stepDesc: {
    fontSize: '0.95rem',
    color: '#6B7280',
    lineHeight: 1.6,
  },
  // Use cases
  useCasesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
  },
  useCaseCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    padding: '32px',
    textAlign: 'center' as const,
  },
  useCaseEmoji: {
    fontSize: '2.5rem',
    marginBottom: '16px',
  },
  useCaseTitle: {
    fontSize: '1.15rem',
    fontWeight: 600,
    marginBottom: '10px',
    color: '#1F2937',
  },
  useCaseDesc: {
    fontSize: '0.95rem',
    color: '#6B7280',
    lineHeight: 1.6,
  },
  // Pricing teaser
  pricingTeaser: {
    textAlign: 'center' as const,
    padding: '60px 24px',
    backgroundColor: '#F0FDF4',
    borderRadius: '16px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  pricingTitle: {
    fontSize: '1.8rem',
    fontWeight: 700,
    marginBottom: '12px',
    color: '#1F2937',
  },
  pricingDesc: {
    fontSize: '1.05rem',
    color: '#6B7280',
    marginBottom: '24px',
    lineHeight: 1.6,
  },
  // Footer
  footer: {
    backgroundColor: '#1F2937',
    color: '#FFFFFF',
    padding: '64px 24px 32px',
  },
  footerInner: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  footerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '40px',
    marginBottom: '48px',
  },
  footerColTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: '#9CA3AF',
    marginBottom: '16px',
  },
  footerLink: {
    display: 'block',
    color: '#D1D5DB',
    textDecoration: 'none',
    fontSize: '0.9rem',
    marginBottom: '10px',
  },
  footerBottom: {
    borderTop: '1px solid #374151',
    paddingTop: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    gap: '16px',
  },
  footerCopyright: {
    color: '#9CA3AF',
    fontSize: '0.85rem',
  },
};

function FeatureIcon({ type }: { type: string }) {
  const iconColor = '#4ADE80';
  const icons: Record<string, React.ReactNode> = {
    canvas: <LayoutGrid width={24} height={24} stroke={iconColor} />,
    ai: <Layers width={24} height={24} stroke={iconColor} />,
    reference: <Image width={24} height={24} stroke={iconColor} />,
    sync: <RefreshCw width={24} height={24} stroke={iconColor} />,
    gallery: <Images width={24} height={24} stroke={iconColor} />,
    presets: <SlidersHorizontal width={24} height={24} stroke={iconColor} />,
  };
  return <>{icons[type] || icons.canvas}</>;
}

export function Homepage2() {
  return (
    <div style={styles.page}>
      {/* Navbar */}
      <nav style={styles.nav}>
        <Link to="/">
          <Logo size="md" />
        </Link>
        <div style={styles.navLinks} className="hp2-nav-links">
          <Link to="/pricing" style={styles.navLink}>Pricing</Link>
          <Link to="/contact" style={styles.navLink}>Contact</Link>
          <Link to="/auth" style={styles.navCta}>Get Started</Link>
        </div>
      </nav>

      {/* Hero */}
      <section style={styles.hero}>
        <h1 style={styles.heroH1}>
          Build AI Image Workflows{' '}
          <span style={styles.heroGreen}>Visually</span>
        </h1>
        <p style={styles.heroSubtitle}>
          Connect prompts, reference images, and presets on an intuitive visual canvas.
          Generate stunning AI images with Google Gemini using up to 14 references.
        </p>
        <div style={styles.heroCtas}>
          <Link to="/auth" style={styles.primaryBtn}>
            Get Started Free
            <ArrowRight width={16} height={16} />
          </Link>
          <a href="#how-it-works" style={styles.secondaryBtn}>
            Learn More
            <ChevronDown width={16} height={16} />
          </a>
        </div>
      </section>

      {/* Features */}
      <section style={styles.sectionGray}>
        <div style={styles.sectionInner}>
          <h2 style={styles.sectionTitle}>Everything you need to create</h2>
          <p style={styles.sectionSubtitle}>
            A complete toolkit for AI-powered image generation workflows
          </p>
          <div style={styles.featuresGrid}>
            {[
              { icon: 'canvas', title: 'Visual Canvas', desc: 'Drag-and-drop node-based workflow editor. Place prompts, images, and settings on an infinite canvas and connect them visually.' },
              { icon: 'ai', title: 'AI Image Generation', desc: 'Powered by Google Gemini. Generate high-quality images from text prompts with advanced AI understanding of your creative intent.' },
              { icon: 'reference', title: 'Reference Images', desc: 'Use up to 14 reference images for precise control. Guide AI generation with visual examples for style, composition, and subject.' },
              { icon: 'sync', title: 'Real-time Sync', desc: 'Multi-browser canvas collaboration. Work on the same canvas across devices with instant synchronization of all changes.' },
              { icon: 'gallery', title: 'Gallery', desc: 'Organize and manage all your generated images in one place. Browse, search, and curate your AI-created visual library.' },
              { icon: 'presets', title: 'Presets', desc: 'Save and reuse size, style, and generation configurations. Create consistent outputs by storing your preferred settings.' },
            ].map((feature) => (
              <div key={feature.title} style={styles.featureCard}>
                <div style={styles.featureIcon}>
                  <FeatureIcon type={feature.icon} />
                </div>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDesc}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" style={styles.section}>
        <h2 style={styles.sectionTitle}>How It Works</h2>
        <p style={styles.sectionSubtitle}>
          Three simple steps to generate AI images
        </p>
        <div style={styles.stepsGrid}>
          {[
            { num: '1', title: 'Create Nodes', desc: 'Add prompt nodes for text descriptions, upload reference images, and configure preset nodes for size and style settings.' },
            { num: '2', title: 'Connect Your Workflow', desc: 'Draw connections between nodes to build your generation pipeline. Link references and prompts to a generate node.' },
            { num: '3', title: 'Generate with One Click', desc: 'Hit generate and watch as Google Gemini creates AI images informed by all your connected prompts and references.' },
          ].map((step) => (
            <div key={step.num} style={styles.stepCard}>
              <div style={styles.stepNumber}>{step.num}</div>
              <h3 style={styles.stepTitle}>{step.title}</h3>
              <p style={styles.stepDesc}>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section style={styles.sectionGray}>
        <div style={styles.sectionInner}>
          <h2 style={styles.sectionTitle}>Built for Creatives</h2>
          <p style={styles.sectionSubtitle}>
            See how teams use Cascady to transform their creative workflow
          </p>
          <div style={styles.useCasesGrid}>
            {[
              { emoji: '🎨', title: 'Designers', desc: 'Create mood boards and iterate on visual concepts rapidly. Use reference images to maintain style consistency across projects.' },
              { emoji: '📢', title: 'Marketers', desc: 'Generate campaign visuals at scale. Produce on-brand imagery for social media, ads, and content with repeatable workflows.' },
              { emoji: '✍️', title: 'Content Creators', desc: 'Produce unique imagery for any platform. Build visual libraries of AI-generated content tailored to your audience and style.' },
            ].map((uc) => (
              <div key={uc.title} style={styles.useCaseCard}>
                <div style={styles.useCaseEmoji}>{uc.emoji}</div>
                <h3 style={styles.useCaseTitle}>{uc.title}</h3>
                <p style={styles.useCaseDesc}>{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section style={styles.section}>
        <div style={styles.pricingTeaser}>
          <h2 style={styles.pricingTitle}>Simple, transparent pricing</h2>
          <p style={styles.pricingDesc}>
            Start free with generous limits. Upgrade as your creative needs grow.
            No hidden fees, cancel anytime.
          </p>
          <Link to="/pricing" style={styles.primaryBtn}>
            View Pricing Plans
            <ArrowRight width={16} height={16} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <div style={styles.footerGrid}>
            <div>
              <h4 style={styles.footerColTitle}>Product</h4>
              <Link to="/#features" style={styles.footerLink}>Features</Link>
              <Link to="/pricing" style={styles.footerLink}>Pricing</Link>
              <a href="#how-it-works" style={styles.footerLink}>How It Works</a>
              <Link to="/auth" style={styles.footerLink}>Get Started</Link>
            </div>
            <div>
              <h4 style={styles.footerColTitle}>Company</h4>
              <Link to="/contact" style={styles.footerLink}>Contact</Link>
              <a href="#" style={styles.footerLink}>About Us</a>
              <a href="#" style={styles.footerLink}>Careers</a>
              <a href="#" style={styles.footerLink}>Blog</a>
            </div>
            <div>
              <h4 style={styles.footerColTitle}>Resources</h4>
              <a href="#" style={styles.footerLink}>Documentation</a>
              <a href="#" style={styles.footerLink}>API Reference</a>
              <a href="#" style={styles.footerLink}>Tutorials</a>
              <a href="#" style={styles.footerLink}>Community</a>
            </div>
            <div>
              <h4 style={styles.footerColTitle}>Legal</h4>
              <Link to="/privacy" style={styles.footerLink}>Privacy Policy</Link>
              <Link to="/terms" style={styles.footerLink}>Terms of Service</Link>
              <Link to="/refund-policy" style={styles.footerLink}>Refund Policy</Link>
              <Link to="/cookie-policy" style={styles.footerLink}>Cookie Policy</Link>
            </div>
          </div>
          <div style={styles.footerBottom}>
            <span style={styles.footerCopyright}>&copy; {new Date().getFullYear()} Cascady. All rights reserved.</span>
            <div style={{ display: 'flex', gap: '20px' }}>
              <a href="#" style={styles.footerLink} aria-label="Twitter">
                <svg width="20" height="20" fill="#9CA3AF" viewBox="0 0 24 24"><path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/></svg>
              </a>
              <a href="#" style={styles.footerLink} aria-label="GitHub">
                <svg width="20" height="20" fill="#9CA3AF" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/></svg>
              </a>
              <a href="#" style={styles.footerLink} aria-label="LinkedIn">
                <svg width="20" height="20" fill="#9CA3AF" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Responsive styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Source+Sans+Pro:wght@400;600&display=swap');

        @media (max-width: 768px) {
          .hp2-nav-links {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
